import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import type { ChatUser, Channel } from 'app/types/chat';
import { useUser } from 'app/network/user';
import { canUserRateLoan, type Loan } from 'app/types/loan';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet } from 'react-native';

import { useNavigation } from 'app/hooks/navigation';
import { createTheme as createDSTheme } from 'app/shopify';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface RatingProps {
  loan: Loan;
  channelUrl: Channel['channel_url'];
  interlocutor: ChatUser['firstname'];
  interlocutorImage?: ChatUser['profile_url'];
  isLender: boolean;
}

const getRatingState = (loan: Loan, isLender: boolean) => {
  if (!loan.rating) return { hasRated: false, isPositiveRating: false };
  const ratingValues = isLender
    ? [
        loan.rating.lenderRating,
        loan.rating.lenderAssetQualityRating,
        loan.rating.lenderLogisticsReliabilityRating,
        loan.rating.lenderCommunityTrustRating,
      ]
    : [
        loan.rating.borrowerRating,
        loan.rating.borrowerAssetQualityRating,
        loan.rating.borrowerLogisticsReliabilityRating,
        loan.rating.borrowerCommunityTrustRating,
      ];
  const hasRated = ratingValues.some((value) => value !== null && value !== undefined);
  const overallRating = isLender ? loan.rating.lenderRating : loan.rating.borrowerRating;
  return { hasRated, isPositiveRating: hasRated && (overallRating ?? 0) >= 3 };
};

const RatingPrompt = ({ openRating, t }: { openRating: (rating: 'positive' | 'negative') => void; t: TFunction }) => (
  <Column alignItems="center" borderRadius="radius.lg" gap="spacing.general.x2" style={styles.rating} width="100%">
    <Text style={styles.promptTitle} textAlign="center">
      {t('promptFigma.title')}
    </Text>
    <Text style={styles.promptSubtitle} textAlign="center">
      {t('promptFigma.subtitle')}
    </Text>
    <Row gap="spacing.general.x2" style={styles.thumbButtonsRow}>
      <Pressable onPress={() => openRating('positive')} style={styles.thumbButton}>
        <Text style={styles.thumbEmoji}>👍</Text>
      </Pressable>
      <Pressable onPress={() => openRating('negative')} style={styles.thumbButton}>
        <Text style={styles.thumbEmoji}>👎</Text>
      </Pressable>
    </Row>
  </Column>
);

const CompletedRating = ({
  interlocutor,
  isPositiveRating,
  t,
}: {
  interlocutor: string;
  isPositiveRating: boolean;
  t: TFunction;
}) => (
  <Column alignItems="center" borderRadius="radius.lg" gap="spacing.general.x2" style={styles.rating} width="100%">
    <Row
      style={{
        alignItems: 'center',
        paddingHorizontal: theme.spacing['spacing.general.x5'],
        paddingVertical: theme.spacing['spacing.general.x2'],
        borderRadius: 35,
        backgroundColor: isPositiveRating ? '#B8ECE0' : theme.colors['surface.error.subtle'],
      }}
    >
      <Text style={{ fontSize: 24, fontWeight: '700' }}>{isPositiveRating ? '👍' : '👎'}</Text>
    </Row>
    <Text fontSize={16} fontWeight="700">
      {t(`rated.${isPositiveRating ? 'recommended' : 'notRecommended'}`, { name: interlocutor })}
    </Text>
    <Text textAlign="center">{t('ratingPrompt.text')}</Text>
  </Column>
);

export const Rating = ({ channelUrl, interlocutor, interlocutorImage, isLender, loan }: RatingProps) => {
  const { t } = useTranslation('ratings');
  // const { t: tPosts } = useTranslation('posts');
  const navigation = useNavigation();
  const userQuery = useUser();
  const canRate = canUserRateLoan(loan, userQuery.data?.id);

  const { hasRated, isPositiveRating } = getRatingState(loan, isLender);

  const openRating = (initialExperience: 'positive' | 'negative') => {
    navigation.navigate(
      'new-rating',
      {
        loanId: loan.id,
        listingId: loan.listingId,
        channelUrl,
        isLender,
        subjectImage:
          (isLender ? interlocutorImage : (loan.listing.picture ?? loan.listing.pictures?.[0]?.url)) ?? null,
        subjectName: isLender ? interlocutor : loan.listing.item.name,
        initialExperience,
      },
      { pop: true },
    );
  };

  if (!isFeatureEnabled(FeatureFlag.rateAfterLoans) || (!hasRated && !canRate)) {
    return null;
  }

  return (
    <Column>
      {canRate ? (
        <RatingPrompt {...{ openRating, t }} />
      ) : (
        <CompletedRating {...{ interlocutor, isPositiveRating, t }} />
      )}
    </Column>
  );
};

const styles = StyleSheet.create({
  actionButton: {
    borderRadius: 25,
    minHeight: 50,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingVertical: theme.spacing['spacing.general.x2'],
  },
  buttonContent: {
    minWidth: 0,
    width: '100%',
  },
  buttonRow: {
    width: '100%',
  },
  buttonText: {
    flexShrink: 1,
    textAlign: 'center',
  },
  fullButton: {
    width: '100%',
  },
  promptSubtitle: {
    color: theme.colors['text.tertiary'],
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 18,
    maxWidth: '92%',
  },
  promptTitle: {
    color: theme.colors['text.primary'],
    fontSize: 20,
    fontWeight: '700',
    lineHeight: 26,
    maxWidth: '92%',
  },
  rating: {
    backgroundColor: theme.colors['surface.primary-neutral'],
    borderRadius: 20,
    paddingHorizontal: theme.spacing['spacing.general.x5'],
    paddingVertical: theme.spacing['spacing.general.x4'],
  },
  secondaryButton: {
    backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
  },
  thumbButton: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderColor: theme.colors['surface.inverse.tertiary-neutral'],

    borderRadius: 20,
    borderWidth: 1,
    flex: 1,
    justifyContent: 'center',
    minHeight: 54,
  },
  thumbButtonsRow: {
    width: '100%',
  },
  thumbEmoji: {
    fontSize: 26,
  },
});
